import React, { useState } from "react";
import { FileBarChart, Wrench, CalendarClock, Eye, Download, Loader2 } from "lucide-react";
import { savedReportsApi, clientPortalApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDateTime, apiError, prettyStatus, statusTone } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function base64ToBlobUrl(b64) {
  const clean = String(b64).replace(/^data:application\/pdf;base64,/, "");
  const chars = atob(clean);
  const bytes = new Uint8Array(chars.length);
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-slate-800">
        <Icon className="h-5 w-5 text-slate-400" /> {title}
      </h2>
      {children}
    </section>
  );
}

export default function PortalHome() {
  const { user } = useAuth();
  const reports = useAsync(() => savedReportsApi.list(), []);
  const jobs = useAsync(() => clientPortalApi.listMaintenanceJobs(), []);
  const scheduled = useAsync(() => clientPortalApi.listScheduledInspections(), []);
  const [busy, setBusy] = useState(null);

  const openReport = async (r, mode) => {
    setBusy(`${mode}-${r.id}`);
    try {
      const res = await savedReportsApi.download(r.id);
      const b64 = res.data?.pdf_base64;
      if (!b64) throw new Error("No PDF available.");
      const url = base64ToBlobUrl(b64);
      if (mode === "d") {
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data?.file_name || "report.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, "_blank", "noopener");
      }
    } catch (e) {
      toast.error(apiError(e, "Could not open report"));
    } finally {
      setBusy(null);
    }
  };

  const reportList = Array.isArray(reports.data) ? reports.data : [];
  const jobList = Array.isArray(jobs.data) ? jobs.data : [];
  const schedList = Array.isArray(scheduled.data) ? scheduled.data : [];

  return (
    <div>
      <PageHeader
        title={`Welcome, ${(user?.name || "").split(" ")[0] || ""}`.trim()}
        subtitle="Your fire-door reports and works in one place"
        testId="portal-home-header"
      />

      <Section icon={FileBarChart} title="Your reports">
        {reports.loading ? (
          <LoadingState label="Loading reports…" rows={2} />
        ) : reportList.length === 0 ? (
          <EmptyState title="No reports shared yet" message="Reports your contractor shares with you will appear here." testId="portal-reports-empty" />
        ) : (
          <div className="space-y-2" data-testid="portal-reports-list">
            {reportList.map((r) => (
              <Card key={r.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-800">{r.title || r.file_name}</p>
                  <p className="truncate text-sm text-slate-500">{r.building_name ? `${r.building_name} · ` : ""}{fmtDateTime(r.generated_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openReport(r, "v")} disabled={!!busy} data-testid={`portal-report-view-${r.id}`}>
                    {busy === `v-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    <span className="ml-1.5 hidden sm:inline">View</span>
                  </Button>
                  <Button size="sm" onClick={() => openReport(r, "d")} disabled={!!busy} data-testid={`portal-report-download-${r.id}`}>
                    {busy === `d-${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    <span className="ml-1.5 hidden sm:inline">Download</span>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Wrench} title="Maintenance">
        {jobs.loading ? (
          <LoadingState label="Loading works…" rows={2} />
        ) : jobList.length === 0 ? (
          <EmptyState title="No maintenance jobs" message="Remedial works on your buildings will show here." testId="portal-jobs-empty" />
        ) : (
          <div className="space-y-2" data-testid="portal-jobs-list">
            {jobList.map((j) => (
              <Card key={j.id} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-800">{j.title || j.building_name || "Maintenance job"}</p>
                  <p className="truncate text-sm text-slate-500">{j.building_name || ""}</p>
                </div>
                {j.status && <Badge variant="outline" className={statusTone(j.status)}>{prettyStatus(j.status)}</Badge>}
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Section icon={CalendarClock} title="Scheduled inspections">
        {scheduled.loading ? (
          <LoadingState label="Loading schedule…" rows={1} />
        ) : schedList.length === 0 ? (
          <EmptyState title="Nothing scheduled" message="Upcoming inspections will be listed here." testId="portal-scheduled-empty" />
        ) : (
          <div className="space-y-2" data-testid="portal-scheduled-list">
            {schedList.map((s) => (
              <Card key={s.id} className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-800">{s.building_name || "Inspection"}</p>
                  <p className="truncate text-sm text-slate-500">{fmtDate(s.scheduled_for)}</p>
                </div>
                {s.status && <Badge variant="outline" className={statusTone(s.status)}>{prettyStatus(s.status)}</Badge>}
              </Card>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
